<script setup lang="ts">
/**
 * Whether a document is actually usable yet — the one trailing signal every document row must carry.
 *
 * Lifted out of `BrowserItemList` because it was the only surface that had it. The case hub and the
 * client detail page listed the same documents with no status at all, so a file still being processed
 * (or blocked on the org's OCR allowance, or failed outright) rendered there exactly like one that was
 * ready to be searched. In a product whose whole promise is "Marcus can read your documents", a row
 * that looks ready and isn't is a lie the interface tells for free.
 *
 * Renders nothing for a healthy document — the caller shows its date instead, so the trailing slot is
 * never empty and never double-booked.
 */
import { LoaderCircle, AlertCircle, ScanText } from 'lucide-vue-next'
import type { ItemRow } from '~~/shared/items/itemRow'

/**
 * Structural — see `DocumentRow` for why these listings are not all `ItemRow`s. Each field's TYPE is
 * still taken from `ItemRow` by indexed access, so the names and the value spaces stay tied to the
 * column even though the shape deliberately is not `Pick<ItemRow, …>`: an unrelated projection can
 * still satisfy it, but nobody can quietly widen `status` here without widening it there.
 */
export interface DocumentStatusFields {
  status?: ItemRow['status']
  pending_read?: ItemRow['pending_read'] | null
}

const props = defineProps<{ item: DocumentStatusFields }>()

const { t } = useI18n()

const state = computed(() => {
  // Kept in step with `isUnsettled` in ~/lib/documentStatus — that predicate decides WHETHER this
  // renders, this one decides WHAT. A state here that is missing there renders an empty chip.
  if (props.item.status === 'uploading') return 'uploading'
  if (props.item.status === 'processing') return 'processing'
  if (props.item.status === 'error') return 'error'
  if (props.item.status === 'quota_blocked') return 'quota'
  if (props.item.pending_read) return 'ocr'
  return null
})

/** True when this document is not yet usable — lets callers mute the row without re-deriving the set. */
const unsettled = computed(() => state.value !== null)
defineExpose({ unsettled })
</script>

<template>
  <span
    v-if="state === 'uploading'"
    class="inline-flex items-center gap-0.5 whitespace-nowrap text-[10px] font-medium text-primary animate-pulse"
  >
    <LoaderCircle class="h-3 w-3 animate-spin text-primary" />
    {{ t('documents.uploading') }}
  </span>
  <span
    v-else-if="state === 'processing'"
    class="inline-flex items-center gap-0.5 whitespace-nowrap text-[10px] text-warning-ink"
    :title="t('documents.processing')"
  >
    <LoaderCircle class="h-3 w-3 animate-spin" />
    {{ t('documents.processing') }}
  </span>
  <span
    v-else-if="state === 'error'"
    class="inline-flex items-center gap-0.5 whitespace-nowrap text-[10px] text-destructive"
    :title="t('documents.processingError')"
  >
    <AlertCircle class="h-3 w-3" />
    {{ t('documents.statusError') }}
  </span>
  <!-- Not an error: the document is fine, the org's monthly allowance is not. It will process once
       the allowance is raised or the period rolls over. -->
  <span
    v-else-if="state === 'quota'"
    class="inline-flex items-center gap-0.5 whitespace-nowrap text-[10px] text-warning-ink"
    :title="t('documents.quotaBlockedTitle')"
  >
    <AlertCircle class="h-3 w-3" />
    {{ t('documents.quotaBlocked') }}
  </span>
  <!-- An offer, not a fault: this document is intact and readable, it just needs the user to say go.
       The alarm glyph this used to carry put it in the same visual family as the failure and the quota
       block above (#84) — same tone, different meaning. -->
  <span
    v-else-if="state === 'ocr'"
    class="inline-flex items-center gap-0.5 whitespace-nowrap text-[10px] font-medium text-accent-ink"
    :title="t('documents.awaitingOcrTitle')"
  >
    <ScanText class="h-3 w-3" />
    {{ t('documents.awaitingOcr') }}
  </span>
</template>
